import React, { useState, useEffect } from 'react';
import { Department } from '../types/models';
import { DepartmentService } from '../services/departmentService';
import { DepartmentForm } from '../components/forms/DepartmentForm';
import { useCompany } from '../contexts/CompanyContext';

export const DepartmentsPage: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const { selectedCompany, companies } = useCompany();

  useEffect(() => {
    loadDepartments();
  }, [selectedCompany]);

  const getCompanyName = (department: Department) =>
    department.company?.shortName
    || department.company?.fullName
    || companies.find(c => c.id === department.companyId)?.shortName
    || `Компания ${department.companyId}`;

  const loadDepartments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await DepartmentService.getAll();

      if (response.success) {
        setDepartments(response.data);
      } else {
        setError(response.message || 'Ошибка загрузки отделов');
      }
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingDepartment(null);
    setShowForm(true);
  };

  const handleEdit = (department: Department) => {
    setEditingDepartment(department);
    setShowForm(true);
  };

  const handleDelete = async (department: Department) => {
    if (window.confirm(`Вы уверены, что хотите удалить отдел "${department.name}"?`)) {
      try {
        const response = await DepartmentService.delete(department.id);
        if (response.success) {
          loadDepartments(); // Перезагрузить список
        } else {
          setError(response.message || 'Ошибка удаления');
        }
      } catch (err) {
        setError('Ошибка удаления отдела');
      }
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingDepartment(null);
    loadDepartments(); // Перезагрузить список
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingDepartment(null);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Структурные подразделения</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          Добавить структурное подразделение
        </button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Название отдела</th>
              <th>Контрагент</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {departments.map((department) => (
              <tr key={department.id}>
                <td>{department.id}</td>
                <td>{department.name}</td>
                <td>{getCompanyName(department)}</td>
                <td>
                  <button
                    className="btn btn-secondary"
                    style={{ marginRight: '5px' }}
                    onClick={() => handleEdit(department)}
                  >
                    Редактировать
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(department)}
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <DepartmentForm
          department={editingDepartment || undefined}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};
